import { cn } from "@/lib/utils";
import type { ElementType, ReactNode } from "react";

interface GradientTextProps {
  children: ReactNode;
  className?: string;
  as?: ElementType;
  /** Animate a light sweep across the text. */
  shimmer?: boolean;
}

/**
 * Brand gradient headline text (purple glow → bright purple). With `shimmer`,
 * a soft highlight sweeps across on a loop. Falls back to a static gradient
 * for reduced-motion users via the global `motion-reduce` utilities.
 */
export function GradientText({ children, className, as, shimmer = false }: GradientTextProps) {
  // `as` is intentionally polymorphic; cast keeps TS from collapsing props to `never`.
  const Tag = (as ?? "span") as "span";
  return (
    <Tag
      className={cn(
        // pb keeps the gradient background extending under glyph descenders
        // (g, y, p) so bg-clip-text doesn't render them transparent/clipped.
        "inline-block bg-clip-text pb-[0.18em] text-transparent",
        shimmer && "animate-shimmer motion-reduce:animate-none",
        className,
      )}
      style={{
        backgroundImage: shimmer
          ? "linear-gradient(110deg, var(--color-gradient-end) 30%, var(--color-primary-glow) 48%, #ffffff 50%, var(--color-primary-glow) 52%, var(--color-gradient-end) 70%)"
          : "linear-gradient(100deg, var(--color-gradient-start), var(--color-primary), var(--color-gradient-end))",
        backgroundSize: shimmer ? "200% 100%" : "100% 100%",
      }}
    >
      {children}
    </Tag>
  );
}
